import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { BookOpen, Calendar, Download, ExternalLink, ChevronDown, PlayCircle, Music, Sparkles, FileText } from 'lucide-react';
import BibleBooksDialog from '../BibleBooksDialog';
import { FIRST_SUNDAY_SONGS, SEEDBED_LESSONS } from '../../data/kidsPlaylists';
import { LEADER_GUIDE_PDFS, LESSONS_META, findCurrentSundayLesson } from '../../data/kidsCurriculumMeta';
import { formatLocalDate } from '../../utils/date';

const KidsCurriculum = () => {
  const [selectedPart, setSelectedPart] = useState('part1');
  const [lessonsOpen, setLessonsOpen] = useState(false);
  const [songsOpen, setSongsOpen] = useState(false);
  const [lessonsInitialVideoId, setLessonsInitialVideoId] = useState('');

  // Open the correct Leader Guide PDF in a new tab, deep-linking to the lesson page.
  // We open in a new tab (not an in-app modal) because:
  //   1. The CDN sends `X-Frame-Options: SAMEORIGIN`, blocking iframe embedding.
  //   2. Each Leader Guide is 40-80MB, too large for Google Docs Viewer (25MB limit).
  // The browser's native PDF viewer honors `#page=N` for one-click jump to the lesson.
  const openPdfAt = (pdfPart, page) => {
    const pdf = LEADER_GUIDE_PDFS[pdfPart];
    if (!pdf) return;
    const href = page ? `${pdf.url}#page=${page}` : pdf.url;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const playLessonVideo = (videoId) => {
    setLessonsInitialVideoId(videoId);
    setLessonsOpen(true);
  };

  // Curriculum schedule for 2nd-4th Sundays starting January 2026
  const curriculumSchedule = [
    // January 2026
    { date: '2026-01-11', lesson: 1, part: 'part1', sunday: '2nd Sunday' },
    { date: '2026-01-18', lesson: 2, part: 'part1', sunday: '3rd Sunday' },
    { date: '2026-01-25', lesson: 3, part: 'part1', sunday: '4th Sunday' },
    
    // February 2026
    { date: '2026-02-08', lesson: 4, part: 'part1', sunday: '2nd Sunday' },
    { date: '2026-02-15', lesson: 5, part: 'part1', sunday: '3rd Sunday' },
    { date: '2026-02-22', lesson: 6, part: 'part1', sunday: '4th Sunday' },
    
    // March 2026
    { date: '2026-03-08', lesson: 7, part: 'part1', sunday: '2nd Sunday' },
    { date: '2026-03-15', lesson: 8, part: 'part1', sunday: '3rd Sunday' },
    { date: '2026-03-22', lesson: 9, part: 'part1', sunday: '4th Sunday' },
    
    // April 2026
    { date: '2026-04-12', lesson: 10, part: 'part1', sunday: '2nd Sunday' },
    { date: '2026-04-19', lesson: 11, part: 'part1', sunday: '3rd Sunday' },
    { date: '2026-04-26', lesson: 12, part: 'part1', sunday: '4th Sunday' },
    
    // May 2026
    { date: '2026-05-10', lesson: 13, part: 'part1', sunday: '2nd Sunday' },
    { date: '2026-05-17', lesson: 14, part: 'part1', sunday: '3rd Sunday' },
    { date: '2026-05-24', lesson: 15, part: 'part2', sunday: '4th Sunday' },
    
    // June 2026
    { date: '2026-06-14', lesson: 16, part: 'part2', sunday: '2nd Sunday' },
    { date: '2026-06-21', lesson: 17, part: 'part2', sunday: '3rd Sunday' },
    { date: '2026-06-28', lesson: 18, part: 'part2', sunday: '4th Sunday' },
    
    // July 2026
    { date: '2026-07-12', lesson: 19, part: 'part2', sunday: '2nd Sunday' },
    { date: '2026-07-19', lesson: 20, part: 'part2', sunday: '3rd Sunday' },
    { date: '2026-07-26', lesson: 21, part: 'part2', sunday: '4th Sunday' },
    
    // August 2026
    { date: '2026-08-09', lesson: 22, part: 'part2', sunday: '2nd Sunday' },
    { date: '2026-08-16', lesson: 23, part: 'part2', sunday: '3rd Sunday' },
    { date: '2026-08-23', lesson: 24, part: 'part2', sunday: '4th Sunday' },
    
    // September 2026
    { date: '2026-09-13', lesson: 25, part: 'part2', sunday: '2nd Sunday' },
    { date: '2026-09-20', lesson: 26, part: 'part2', sunday: '3rd Sunday' },
    { date: '2026-09-27', lesson: 27, part: 'part2', sunday: '4th Sunday' },
    // October 2026
    { date: '2026-10-11', lesson: 28, part: 'part3', sunday: '2nd Sunday' },
    { date: '2026-10-18', lesson: 29, part: 'part3', sunday: '3rd Sunday' },
    { date: '2026-10-25', lesson: 30, part: 'part3', sunday: '4th Sunday' },
    // November 2026
    { date: '2026-11-08', lesson: 31, part: 'part3', sunday: '2nd Sunday' },
    { date: '2026-11-15', lesson: 32, part: 'part3', sunday: '3rd Sunday' },
    { date: '2026-11-22', lesson: 33, part: 'part3', sunday: '4th Sunday' },
    // December 2026
    { date: '2026-12-13', lesson: 34, part: 'part3', sunday: '2nd Sunday' },
    { date: '2026-12-20', lesson: 35, part: 'part3', sunday: '3rd Sunday' },
    { date: '2026-12-27', lesson: 36, part: 'part3', sunday: '4th Sunday' },
    // January 2027 — carries the arc into the new year with Lessons 37-39
    { date: '2027-01-10', lesson: 37, part: 'part3', sunday: '2nd Sunday' },
    { date: '2027-01-17', lesson: 38, part: 'part3', sunday: '3rd Sunday' },
    { date: '2027-01-24', lesson: 39, part: 'part3', sunday: '4th Sunday' },
    // February 2027 — finale of the 40-lesson arc
    { date: '2027-02-14', lesson: 40, part: 'part3', sunday: '2nd Sunday' },
  ];

  const part1Lessons = [
    { num: 1, title: "Trackers Know Jesus", topic: "Jesus is the Promised One", verse: "John 14:6" },
    { num: 2, title: "Trackers Believe", topic: "Zechariah and Elizabeth Fulfill Prophecy", verse: "Colossians 3:2-3" },
    { num: 3, title: "Trackers Hear the Voice of the Father", topic: "John Baptizes Jesus", verse: "John 14:6" },
    { num: 4, title: "Trackers Are Tested", topic: "Jesus Is Tested in the Wilderness", verse: "2 Timothy 3:16-17" },
    { num: 5, title: "Trackers Follow Jesus", topic: "Jesus Calls the Twelve Disciples", verse: "Matthew 16:25" },
    { num: 6, title: "Trackers Listen to the Teachings of Jesus", topic: "Jesus Gives the Sermon on the Mount", verse: "Matthew 5:8" },
    { num: 7, title: "Trackers Have Faith", topic: "Jesus Heals the Sick", verse: "Matthew 17:20" },
    { num: 8, title: "Trackers Are Forgiven", topic: "Jesus Talks to the Woman at the Well", verse: "Psalm 63:3–4" },
    { num: 9, title: "Trackers Are Sent", topic: "Jesus Sends Out His Disciples", verse: "John 13:16–17" },
    { num: 10, title: "Trackers Offer Themselves to God", topic: "Jesus Multiplies the Fish and the Loaves", verse: "Philippians 4:6" },
    { num: 11, title: "Trackers Endure the Storms of Life", topic: "Jesus Walks on Water", verse: "Psalm 46:1" },
    { num: 12, title: "Trackers Embrace the Holiness of God", topic: "Jesus is on the Mount of Transfiguration", verse: "Psalm 148:1–2" },
    { num: 13, title: "Trackers Trust the Good Shepherd", topic: "Jesus is the Good Shepherd", verse: "Psalm 100:1–2" },
    { num: 14, title: "Trackers Are Found by the Good Shepherd", topic: "Jesus Looks for the Lost Sheep", verse: "Psalm 105:1–3" },
  ];

  const part2Lessons = [
    { num: 15, title: "Trackers Are Welcomed Back", topic: "Parable of the Prodigal Son", verse: "Luke 15:11–31" },
    { num: 16, title: "Trackers Are Compassionate", topic: "Parable of the Good Samaritan", verse: "Luke 10:25–37" },
    { num: 17, title: "Trackers Are Friends of God", topic: "Jesus visits Mary and Martha", verse: "Luke 10, John 12" },
    { num: 18, title: "Trackers Rise Again", topic: "Jesus raises Lazarus from the dead", verse: "John 11:1–44" },
    { num: 19, title: "Trackers Strive to See God", topic: "Jesus goes to Zacchaeus's house", verse: "Luke 19" },
    { num: 20, title: "Trackers Remember Jesus' Sacrifice", topic: "The Last Supper and the Cross", verse: "Luke 22–24" },
    { num: 21, title: "Trackers Have Relationship with the Lamb of God", topic: "Jesus' death and resurrection", verse: "John 1:29, Rev 5" },
    { num: 22, title: "Trackers Share the Good News", topic: "Ascension and the Great Commission", verse: "Acts 1, Matt 28" },
    { num: 23, title: "Trackers Are Anointed", topic: "The Holy Spirit comes at Pentecost", verse: "Acts 2" },
    { num: 24, title: "Trackers Have Fellowship with One Another", topic: "The Church begins", verse: "Acts 2–4" },
    { num: 25, title: "Trackers Walk with and Praise God", topic: "Peter and John heal a crippled man", verse: "Acts 3" },
    { num: 26, title: "Trackers Sometimes Suffer", topic: "Stephen becomes the first martyr", verse: "Acts 6–7" },
    { num: 27, title: "Trackers Are Changed", topic: "Saul becomes Paul the Apostle", verse: "Acts 9" },
  ];

  const part3Lessons = [
    { num: 28, title: "Trackers Respond to What Jesus Has Done for Them", topic: "Living in gratitude for the gospel", verse: "Romans 12:1-2" },
    { num: 29, title: "Trackers Live on Mission", topic: "Paul's missionary journeys begin", verse: "Acts 13-14" },
    { num: 30, title: "Trackers Have Eternal Life in Jesus", topic: "Assurance of salvation in Christ", verse: "John 3:16, 1 John 5:11-13" },
    { num: 31, title: "Trackers All Have Spiritual Gifts", topic: "Every believer is gifted to serve", verse: "1 Corinthians 12" },
    { num: 32, title: "Trackers Have the Fruit of the Spirit", topic: "The Spirit grows Christlike character", verse: "Galatians 5:22-23" },
    { num: 33, title: "Trackers Put on the Armor of God", topic: "Standing firm against the enemy", verse: "Ephesians 6:10-18" },
    { num: 34, title: "Trackers Live with the Future in Mind", topic: "Setting our hope on Christ's return", verse: "Philippians 3:20-21" },
    { num: 35, title: "Trackers Are a Godly Example for Others", topic: "Being an example to fellow believers", verse: "1 Timothy 4:12" },
    { num: 36, title: "Trackers Walk by Faith", topic: "Trusting God through every season", verse: "Hebrews 11" },
    { num: 37, title: "Trackers Show Their Faith through Love", topic: "Faith expressed in loving action", verse: "James 2:14-26" },
    { num: 38, title: "Trackers Surrender All to Jesus", topic: "Whole-life devotion to Christ", verse: "1 Peter 5:6-11" },
    { num: 39, title: "Trackers Walk in Light and Love", topic: "Walking in the light as He is in the light", verse: "1 John 1:5-7, 4:7-12" },
    { num: 40, title: "Trackers Look for Jesus to Return", topic: "The blessed hope of Christ's return", verse: "Revelation 22" },
  ];

  const pdfResources = [
    { part: 1, ...LEADER_GUIDE_PDFS[1] },
    { part: 2, ...LEADER_GUIDE_PDFS[2] },
    { part: 3, ...LEADER_GUIDE_PDFS[3] },
  ];

  // Figure out "this Sunday's" lesson (next scheduled Sunday, or today if today is one).
  const currentSunday = useMemo(() => findCurrentSundayLesson(curriculumSchedule), [curriculumSchedule]);

  const getLessonForDate = (date) => {
    return curriculumSchedule.find(s => s.date === date);
  };

  // Parse "YYYY-MM-DD" as a local-timezone date, NOT UTC. Otherwise
  // `new Date('2026-01-11')` becomes UTC midnight → shifts back to Saturday
  // in western hemisphere timezones and misreports every Sunday as a Saturday.
  const formatDate = (dateStr) => formatLocalDate(dateStr, { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-t-4 border-t-[#D4A574]">
        <CardHeader>
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-[#D4A574]" />
            <div>
              <CardTitle className="text-3xl font-heading text-[#2B6B7F]">
                Kids Curriculum: Trackers of Faith
              </CardTitle>
              <CardDescription className="text-base mt-2">
                New Testament curriculum for children • Scheduled for 2nd-4th Sundays each month
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-slate-800 mb-2">About This Curriculum</h4>
            <p className="text-sm text-slate-700 leading-relaxed mb-3">
              "Trackers of Faith" is a comprehensive 40-lesson journey through the New Testament designed for children K-6th grade. 
              Lessons are taught on the <strong>2nd, 3rd, and 4th Sundays</strong> of each month, beginning January 2026.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-[#2B6B7F]">40 Lessons</Badge>
              <Badge className="bg-[#5FA8C1]">Ages K-6</Badge>
              <Badge className="bg-[#7BA888]">3 Sundays/Month</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Watch the current lessons + First Sunday Songs */}
      <Card className="border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-purple-50">
        <CardHeader>
          <CardTitle className="text-xl font-heading text-pink-800 flex items-center gap-2">
            <PlayCircle className="w-5 h-5" />
            Watch Along at Home
          </CardTitle>
          <CardDescription>
            Stay caught up with every Sunday&apos;s animated lesson and let the kids dance along to First-Sunday worship songs during the week.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* THIS SUNDAY spotlight — auto-picks the next scheduled Sunday's lesson */}
          {currentSunday && LESSONS_META[currentSunday.lesson] && (() => {
            const meta = LESSONS_META[currentSunday.lesson];
            return (
              <div
                className="rounded-xl bg-white border border-amber-300 p-4 shadow-sm"
                data-testid="this-sunday-card"
              >
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-700 mb-1">
                  <Sparkles className="w-4 h-4" />
                  Last Sunday · {formatDate(currentSunday.date)}
                </div>
                <p className="text-lg font-heading font-semibold text-slate-800 leading-tight">
                  Lesson {currentSunday.lesson}: {meta.title}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">Missed it? Catch up below.</p>
                <div className="grid sm:grid-cols-2 gap-2 mt-3">
                  <Button
                    onClick={() => playLessonVideo(meta.videoId)}
                    className="justify-start bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="this-sunday-watch-btn"
                  >
                    <PlayCircle className="w-4 h-4 mr-2" />
                    Watch this lesson&apos;s video
                  </Button>
                  <Button
                    onClick={() => openPdfAt(meta.pdfPart, meta.pdfPage)}
                    variant="outline"
                    className="justify-start border-[#2B6B7F] text-[#2B6B7F] hover:bg-[#2B6B7F]/5"
                    data-testid="this-sunday-guide-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Open the Leader Guide
                  </Button>
                </div>
              </div>
            );
          })()}

          <div className="grid md:grid-cols-2 gap-3">
            <Button
              onClick={() => { setLessonsInitialVideoId(''); setLessonsOpen(true); }}
              className="h-auto py-4 bg-purple-600 hover:bg-purple-700 text-white justify-start"
              data-testid="open-kids-lessons-dialog"
            >
              <PlayCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              <div className="text-left flex-1">
                <div className="font-semibold">Animated Video Lessons</div>
                <div className="text-xs opacity-90 font-normal">{SEEDBED_LESSONS.length} videos · every Sunday&apos;s teaching</div>
              </div>
            </Button>
            <Button
              onClick={() => setSongsOpen(true)}
              className="h-auto py-4 bg-pink-600 hover:bg-pink-700 text-white justify-start"
              data-testid="open-kids-songs-dialog"
            >
              <Music className="w-5 h-5 mr-2 flex-shrink-0" />
              <div className="text-left flex-1">
                <div className="font-semibold">First Sunday Songs</div>
                <div className="text-xs opacity-90 font-normal">{FIRST_SUNDAY_SONGS.length} worship songs with motions</div>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Download Resources — leader guides with clickable TOC */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-heading text-[#2B6B7F]">
            Leader Guide PDFs
          </CardTitle>
          <CardDescription>
            Tap a guide to open it in a viewer with the clickable Table of Contents — or download it for offline use.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {pdfResources.map((resource) => (
            <div
              key={resource.part}
              className="p-4 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
              data-testid={`leader-guide-row-${resource.part}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <h4 className="font-semibold text-slate-800 mb-1">{resource.title}</h4>
                  <p className="text-sm text-slate-600">{resource.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => openPdfAt(resource.part, null)}
                    variant="outline"
                    size="sm"
                    className="border-[#2B6B7F] text-[#2B6B7F] hover:bg-[#2B6B7F]/5"
                    data-testid={`view-leader-guide-${resource.part}`}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    View
                  </Button>
                  <a
                    href={resource.url}
                    download
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#2B6B7F] text-white rounded-md hover:bg-[#1E4D5F] text-sm font-medium whitespace-nowrap"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </a>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Curriculum Schedule Calendar View */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-heading text-[#2B6B7F]">
            Teaching Schedule
          </CardTitle>
          <CardDescription>
            Lessons scheduled for 2nd, 3rd, and 4th Sundays — January 2026 through February 2027
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {(() => {
              const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
              // Build unique year-month groups in chronological order from the schedule itself.
              const groups = [];
              const seen = new Set();
              curriculumSchedule.forEach((s) => {
                const key = s.date.slice(0, 7); // YYYY-MM
                if (!seen.has(key)) {
                  seen.add(key);
                  const [yr, mo] = key.split('-').map(Number);
                  groups.push({ key, year: yr, month: mo, label: `${monthNames[mo - 1]} ${yr}` });
                }
              });

              return groups.map((g) => {
                const monthSchedule = curriculumSchedule.filter((s) => s.date.slice(0, 7) === g.key);
                return (
                  <div key={g.key} className="border-l-4 border-l-[#5FA8C1] pl-4">
                    <h3 className="font-semibold text-lg text-[#2B6B7F] mb-3">{g.label}</h3>
                    <div className="space-y-2">
                      {monthSchedule.map((schedule, sIdx) => {
                        const lesson = schedule.part === 'part1'
                          ? part1Lessons.find(l => l.num === schedule.lesson)
                          : schedule.part === 'part2'
                            ? part2Lessons.find(l => l.num === schedule.lesson)
                            : part3Lessons.find(l => l.num === schedule.lesson);
                        const meta = LESSONS_META[schedule.lesson];
                        const isThisSunday = currentSunday && schedule.date === currentSunday.date;

                        return (
                          <button
                            key={sIdx}
                            type="button"
                            onClick={() => meta && openPdfAt(meta.pdfPart, meta.pdfPage)}
                            disabled={!meta}
                            className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${
                              isThisSunday
                                ? 'bg-amber-50 border-2 border-amber-300 hover:bg-amber-100'
                                : 'bg-slate-50 hover:bg-slate-100 border border-transparent'
                            } ${meta ? 'cursor-pointer' : 'cursor-default opacity-70'}`}
                            data-testid={`schedule-row-${schedule.date}`}
                          >
                            <Calendar className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isThisSunday ? 'text-amber-600' : 'text-[#5FA8C1]'}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="font-medium text-slate-800">
                                  {formatDate(schedule.date)}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  {schedule.sunday}
                                </Badge>
                                {isThisSunday && (
                                  <Badge className="bg-amber-500 text-white text-xs">Last Sunday</Badge>
                                )}
                              </div>
                              <p className="text-sm font-semibold text-[#2B6B7F]">
                                Lesson {schedule.lesson}: {lesson?.title}
                              </p>
                              <p className="text-xs text-slate-600">{lesson?.topic}</p>
                              {meta && (
                                <p className="text-xs text-teal-700 mt-1 flex items-center gap-1">
                                  <FileText className="w-3 h-3" />
                                  Tap to open the Leader Guide at this lesson (new tab)
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            });
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Lesson Details by Part */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-heading text-[#2B6B7F]">
            Complete Lesson Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-6">
            <Button
              onClick={() => setSelectedPart('part1')}
              variant={selectedPart === 'part1' ? 'default' : 'outline'}
              className={selectedPart === 'part1' ? 'bg-[#2B6B7F]' : ''}
            >
              Part 1 (Lessons 1-14)
            </Button>
            <Button
              onClick={() => setSelectedPart('part2')}
              variant={selectedPart === 'part2' ? 'default' : 'outline'}
              className={selectedPart === 'part2' ? 'bg-[#2B6B7F]' : ''}
            >
              Part 2 (Lessons 15-27)
            </Button>
            <Button
              onClick={() => setSelectedPart('part3')}
              variant={selectedPart === 'part3' ? 'default' : 'outline'}
              className={selectedPart === 'part3' ? 'bg-[#2B6B7F]' : ''}
            >
              Part 3 (Lessons 28-40)
            </Button>
          </div>

          <div className="space-y-3">
            {selectedPart === 'part1' && part1Lessons.map((lesson) => (
              <div key={lesson.num} className="p-4 rounded-lg border border-slate-200">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#2B6B7F] text-white flex items-center justify-center font-bold flex-shrink-0">
                    {lesson.num}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-800 mb-1">{lesson.title}</h4>
                    <p className="text-sm text-slate-600 mb-2">{lesson.topic}</p>
                    <p className="text-xs text-slate-500">
                      <strong>Memory Verse:</strong> {lesson.verse}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {selectedPart === 'part2' && part2Lessons.map((lesson) => (
              <div key={lesson.num} className="p-4 rounded-lg border border-slate-200">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#5FA8C1] text-white flex items-center justify-center font-bold flex-shrink-0">
                    {lesson.num}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-800 mb-1">{lesson.title}</h4>
                    <p className="text-sm text-slate-600 mb-2">{lesson.topic}</p>
                    <p className="text-xs text-slate-500">
                      <strong>Bible Passage:</strong> {lesson.verse}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {selectedPart === 'part3' && part3Lessons.map((lesson) => (
              <div key={lesson.num} className="p-4 rounded-lg border border-slate-200">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                    {lesson.num}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-800 mb-1">{lesson.title}</h4>
                    <p className="text-sm text-slate-600 mb-2">{lesson.topic}</p>
                    <p className="text-xs text-slate-500">
                      <strong>Memory Verse:</strong> {lesson.verse}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Video dialogs — reusing the Bible Books grid modal for the kids playlists */}
      <BibleBooksDialog
        open={lessonsOpen}
        onOpenChange={(v) => { setLessonsOpen(v); if (!v) setLessonsInitialVideoId(''); }}
        title="Kids Animated Video Lessons"
        subtitle={`Every Sunday's animated lesson from the Trackers of Faith curriculum. Tap any thumbnail to play. ${SEEDBED_LESSONS.length} videos.`}
        searchPlaceholder="Search a lesson (e.g. David, Esther, NT 5)"
        videos={SEEDBED_LESSONS}
        playlistUrl="https://www.youtube.com/playlist?list=PL8126j_-PzM-NutWFhJiz5F6IgOqo9Fd0"
        accent="text-purple-700"
        initialVideoId={lessonsInitialVideoId}
      />
      <BibleBooksDialog
        open={songsOpen}
        onOpenChange={setSongsOpen}
        title="First Sunday Songs"
        subtitle={`Worship songs kids can sing and dance along to at home. Tap any thumbnail to play. ${FIRST_SUNDAY_SONGS.length} videos.`}
        searchPlaceholder="Search a song (e.g. Praise, King of Kings)"
        videos={FIRST_SUNDAY_SONGS}
        playlistUrl="https://www.youtube.com/playlist?list=PL8126j_-PzM9OK1bCAZlo8PSO4SGfyVvJ"
        accent="text-pink-700"
      />
    </div>
  );
};

export default KidsCurriculum;
